/**
 * Where a signed-out visitor was going, carried through the login page in its query string
 * so the link they opened still lands them there once they are in.
 *
 * The query string rather than storage: it survives the login page's own reloads (the
 * language and dark-mode toggles), it is scoped to this one tab, and a stale value cannot
 * outlive the login it was meant for.
 */
export const LOGIN_REDIRECT_PARAM = "redirect";

export const LOGIN_PATH = "/login";

/** Arrivals that carry no destination worth returning to. */
const NO_DESTINATION_PATHS = ["/", LOGIN_PATH];

/**
 * The login URL for a signed-out arrival at `location` (react-router's location object),
 * keeping the path, query and fragment they asked for.
 */
export const loginUrlFor = ({ pathname, search, hash }) => {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (NO_DESTINATION_PATHS.includes(path)) return LOGIN_PATH;

  const destination = `${pathname}${search}${hash}`;
  return `${LOGIN_PATH}?${LOGIN_REDIRECT_PARAM}=${encodeURIComponent(destination)}`;
};

/**
 * The page to open once signed in: the saved destination when it is a path on this site,
 * otherwise `fallback`.
 *
 * Anything else is refused, because the value comes from a link anyone can write — without
 * the check `/login?redirect=https://evil.example` would forward a freshly signed-in user
 * off-site. `//evil.example` and `/\evil.example` are protocol-relative in browsers, so a
 * leading slash alone does not prove a path; resolving against our own origin does.
 *
 * The same-origin check covers the input, not the output. Resolving collapses dot segments,
 * so `/.//evil.example` and `/a/..//evil.example` are same-origin URLs whose pathname is
 * `//evil.example` — protocol-relative again once handed back as a string. A path that
 * still opens with two slashes is refused for that reason; no page of ours starts with one.
 */
export const resolvePostLoginPath = (redirect, fallback) => {
  if (typeof redirect !== "string" || !redirect.startsWith("/")) {
    return fallback;
  }

  let target;
  try {
    target = new URL(redirect, window.location.origin);
  } catch {
    return fallback;
  }

  if (target.origin !== window.location.origin) return fallback;
  if (target.pathname.startsWith("//")) return fallback;
  if (target.pathname.replace(/\/+$/, "") === LOGIN_PATH) return fallback;

  return `${target.pathname}${target.search}${target.hash}`;
};
